import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AlertStatus, ToastProps } from '@chakra-ui/react';
import { Alert, AlertDescription, AlertTitle, Box, CloseButton } from '@chakra-ui/react';

import type { MessageType } from '@izimi/modules';

import type { FeatherIconName } from '../icon/FeatherIcon';
import FeatherIcon from '../icon/FeatherIcon';

type Props = {
  icon: FeatherIconName;
  title: MessageType<FeatherIconName>['message'];
  status: AlertStatus;
  translateTitle?: boolean;
} & Omit<ToastProps, 'title' | 'translate'>;

const Message = ({
  status,
  id,
  title,
  translateTitle = true,
  onClose,
  description,
  icon,
}: Props) => {
  const { t } = useTranslation();
  const alertTitleId = typeof id !== 'undefined' ? `toast-${id}-title` : undefined;

  const alertTitle = useMemo(() => {
    const isTitleString = typeof title === 'string';

    if (isTitleString && !translateTitle) {
      return title;
    }

    return t(isTitleString ? title : title?.[0], isTitleString ? undefined : title?.[1]);
  }, [title, translateTitle, t]);

  return (
    <Alert
      status={status}
      variant='solid'
      id={id as string | undefined}
      alignItems='center'
      borderRadius='md'
      boxShadow='lg'
      paddingEnd={8}
      textAlign='start'
      width='auto'
      aria-labelledby={alertTitleId}
      color='white'
      backgroundColor='neutral600'>
      <FeatherIcon name={icon} size={18} />
      <Box flex='1' maxWidth='100%'>
        <AlertTitle id={alertTitleId} px={2}>
          {alertTitle}
        </AlertTitle>
        {description && <AlertDescription display='block'>{description}</AlertDescription>}
      </Box>
      <CloseButton size='sm' onClick={onClose} position='absolute' insetEnd={1} top={1} />
    </Alert>
  );
};

export default Message;
